/* istanbul ignore file */
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ADPResourceService } from 'digital-core';
import { Observable } from 'rxjs';
import { PreInitSettingsService } from './settings/pre-init-setting.service';
import { PGHAppLoadService } from './pgh-app-load.service';
import { IRegisteredDevice } from 'src/app/model/claims/IRegisteredDevice';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ServicesRequestsModalWindowComponent } from 'src/app/modules/dashboard/components/services-requests-modal-window/services-requests-modal-window.component';
import { CoverageEndedServiceModalComponent } from 'src/app/shared/components/coverage-ended-service-modal/coverage-ended-service-modal.component';
import { AppConstants } from '../constants';
import { PlanDetail } from 'src/app/model/plan/plan-detail';
import { CacheEntry } from 'src/app/model/cache-entry/cache-entry';
import { StorageService } from './storage.service';
import { PGHOktaService } from './pghokta.service';

@Injectable({
  providedIn: 'root',
})
export class GSSPAuthenticationService {
  private matDialog = inject(MatDialog);
  expApiUrl: string;
  portalUrl: string;
  clientName: string;
  constructor(
    private http: HttpClient,
    private preInitSettingsService: PreInitSettingsService,
    private appLoadService: PGHAppLoadService,
    private resourceService: ADPResourceService,
    private storageService: StorageService,
    private oktaService: PGHOktaService
  ) {
    this.expApiUrl = this.preInitSettingsService.getSettings().middleWare.middlewareUrl + this.preInitSettingsService.getSettings().middleWare.accountPath + 'gsspauthtoken';
    this.appLoadService.isSiteResourcesLoaded.subscribe(value => {
      if (value) {
        this.portalUrl = this.resourceService.getPortalSetting("URL_ClaimsPortal").Value;
        this.clientName = this.resourceService.getPortalSetting("Global_SourceClientName").Value;
      }
    });
  }

  postGenerateGSSPToken(dealerCode: string, certificateNumber: string, childCertificateNumbers: string[], registeredDevice: IRegisteredDevice, claimNumber: string = null): Observable<any> {
    const cacheKey = `${AppConstants.STORAGE_KEYS.PlanDetail}:${certificateNumber}`;
    const cacheEntry = this.storageService.getSession<CacheEntry<PlanDetail>>(cacheKey);
    const oktaUserId = this.oktaService.getIdToken()?.claims?.sub ?? null;
    const body = {
      ClientName: this.clientName,
      TokenRequestInformation: {
        userIdentifier: oktaUserId,
        DealerCode: dealerCode,
        CertificateNumber: certificateNumber,
        ClaimNumber: claimNumber,
        ChildCertificateNumbers: childCertificateNumbers,
        PlanType: this.resourceService.getViewLabel(`MyPlan_${cacheEntry.data.planType}_Protection_Label`),
        Product: registeredDevice == null ? null : {
          certificateNumber: registeredDevice.certificateNumber ?? childCertificateNumbers[0],
          manufacturer: registeredDevice.manufacturer,
          make: registeredDevice.manufacturer,
          model: registeredDevice.model,
          imei: '',
          serialNumber: registeredDevice.serialNumber,
          skuNumber: '',
          imageUrl: '',
          memory: '',
          color: '',
          size: '',
          deviceType: registeredDevice.itemDescription,
          description: registeredDevice.itemDescription,
          riskTypeCode: '',
          riskTypeDescription: '',
          isMobile: 'false',
          name: registeredDevice.registeredItemName,
          registeredName: registeredDevice.registeredItemName,
          registeredDevices: '',
          type: registeredDevice.deviceTypeCode,
          purchaseDate: registeredDevice.purchasedDate,
          purchasePrice: registeredDevice.purchasePrice,          
        },
        Customer : registeredDevice == null ? null : {
          firstName: registeredDevice.firstName ?? '',
          lastName: registeredDevice.lastName ?? '',
          homePhone: registeredDevice.homePhone ?? {},
          cellPhone: registeredDevice.cellPhone ?? {},
          email: registeredDevice.email ?? '',
          address: registeredDevice.address ?? '',
          city: registeredDevice.city ?? '',
          country: registeredDevice.country ?? '',
          postalCode: registeredDevice.postalCode ?? '',
          state: registeredDevice.state ?? '',
        },
        preSelectedQuestion: registeredDevice == null ? null : {
          coverageType: registeredDevice.coverageTypeCode,
          flowType: registeredDevice.serviceType
        },
      }
    };
    return this.http.post<IRegisteredDevice>(this.expApiUrl, body);
  }

  openServiceRequestModal(title = 'Dashboard_ServiceRequest_Modal_Title', description = 'Dashboard_ServiceRequest_Modal_Description'): void {
    this.matDialog.open(ServicesRequestsModalWindowComponent, {
      data: {
        model: {
          phoneNumber: this.resourceService.getViewLabel('Global_PhoneNumber'),
        },
        title: title,
        desc: description
      },
      autoFocus: false,
      height: 'auto',
      disableClose: false
    });
  }

  /**
   * Opens the "coverage has ended" warning modal shown to expired users before the GSSP
   * service/claims handoff. The dialog closes with `true` when the user opts to continue.
   */
  openCoverageEndedServiceModal(): MatDialogRef<CoverageEndedServiceModalComponent, boolean> {
    return this.matDialog.open<CoverageEndedServiceModalComponent, undefined, boolean>(CoverageEndedServiceModalComponent, {
      autoFocus: false,
      ariaLabelledBy: 'coverageEndedServiceModalTitle',
    });
  }

  generateFileClaimLink(token: string) {
    return this.portalUrl + '?accessToken=' + token + '&redirectUrl=/file-claim';
  }

  get path(): string | null {
    const isProduction = this.preInitSettingsService.getSettings()?.isProduction?.toLowerCase() === 'true';
    if(isProduction) {
      return null;
    }

    const elitaEnv = this.storageService.getLocal<string>(AppConstants.STORAGE_KEYS.ElitaEnvironment) ?? this.storageService.getLocal<string>(AppConstants.STORAGE_KEYS.ElitaEnvironmentDefault);

    // if elitaenv start with U1/U2/U3 strip the U and return the env number, otherwise return null
    if (elitaEnv && elitaEnv.toUpperCase().startsWith('U')) {
      return elitaEnv.substring(1);
    }

    return null;
  }

  appendPathParam(url: string): string {
    const path = this.path;
    return path ? `${url}&path=${path}` : url;
  }
}
